import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Avatar,
  Button,
  Form,
  Input,
  DatePicker,
  Select,
  Upload,
  Spin,
  Divider,
  App,
} from "antd";
import { EditOutlined, CameraFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import http from "../../services/http";
import { apiGetDoctorProfile } from "../../api/profile";
import { useNavigate } from "react-router-dom";
import ChangePasswordModal from "./ChangePasswordModal";
import "./Profile.css";
import { AppContext } from "../../AppContext";

const { TextArea } = Input;
const { Option } = Select;

// Inside the Profile component, add these helper functions:
const formatNextAvailability = (timestamp) => {
  if (!timestamp) return "Chưa thiết lập";

  const date = dayjs.unix(timestamp);
  const now = dayjs();
  const tomorrow = now.add(1, "day").startOf("day");
  const today = now.startOf("day");

  if (date.isSame(today, "day")) {
    return `Hôm nay, ${date.format("h:mm A")}`;
  } else if (date.isSame(tomorrow, "day")) {
    return `Ngày mai, ${date.format("h:mm A")}`;
  } else {
    return date.format("MMM D, h:mm A");
  }
};

const formatPasswordChanged = (timestamp) => {
  if (!timestamp) return "Never";

  const date = dayjs.unix(timestamp);
  return date.fromNow(); // e.g., "3 months ago"
};

const Profile = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);

  const { state, dispatch } = useContext(AppContext);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await apiGetDoctorProfile();
      const user = res.data;
      setProfileData(user);

      let dateOfBirth = null;
      if (user.dateOfBirth) {
        // Check if it's a Unix timestamp (number)
        if (typeof user.dateOfBirth === "number") {
          dateOfBirth = dayjs.unix(user.dateOfBirth);
        } else {
          // Try parsing as string date
          dateOfBirth = dayjs(
            user.dateOfBirth,
            ["YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY"],
            true
          );
        }

        if (!dateOfBirth.isValid()) {
          dateOfBirth = null;
        }
      }

      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        dateOfBirth: dateOfBirth,
        gender: user.gender,
        affiliatedHospital: user.affiliatedHospital,
        experience: user.experience,
        bio: user.bio,
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoUpload = async (file) => {
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await http.put("/api/doctor/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success("Ảnh đại diện đã được cập nhật thành công");

      // Update profileData state - check the actual API response structure
      const updatedProfileImage =
        response.data?.profileImage || response.profileImage;

      setProfileData((prev) => ({
        ...prev,
        profileImage: updatedProfileImage,
      }));

      // Update localStorage
      const user = JSON.parse(localStorage.getItem("USER") || "{}");
      user.profileImage = updatedProfileImage;
      localStorage.setItem("USER", JSON.stringify(user));

      const updatedUser = {
        ...state.currentUser,
        profileImage: updatedProfileImage,
      };

      dispatch({
        type: "SET_CURRENT_USER",
        data: updatedUser,
      });

      // Force re-render by fetching fresh data
      await fetchProfile();
    } catch (error) {
      console.error("Error uploading photo:", error);
      message.error("Không thể tải ảnh lên");
    } finally {
      setUploadingPhoto(false);
    }
    return false;
  };

  const handleSavePersonalInfo = async () => {
    try {
      const values = await form.validateFields([
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "dateOfBirth",
        "gender",
      ]);

      setSaving(true);
      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("phoneNumber", values.phoneNumber);
      if (values.dateOfBirth) {
        formData.append("dateOfBirth", values.dateOfBirth.format("YYYY-MM-DD"));
      }
      formData.append("gender", values.gender);

      await http.put("/api/doctor/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success("Thông tin cá nhân đã được cập nhật thành công");
      setEditingPersonal(false);
      fetchProfile();
    } catch (error) {
      if (error.errorFields) {
        message.error("Vui lòng điền đầy đủ tất cả các trường bắt buộc");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfessionalInfo = async () => {
    try {
      const values = await form.validateFields([
        "affiliatedHospital",
        "experience",
        "bio",
      ]);

      setSaving(true);
      const formData = new FormData();
      formData.append("affiliatedHospital", values.affiliatedHospital);
      formData.append("experience", values.experience);
      if (values.bio) {
        formData.append("bio", values.bio);
      }

      await http.put("/api/doctor/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success("Thông tin chuyên môn đã được cập nhật thành công");
      setEditingProfessional(false);
      fetchProfile();
    } catch (error) {
      if (error.errorFields) {
        message.error("Vui lòng điền đầy đủ tất cả các trường bắt buộc");
      }
    } finally {
      setSaving(false);
    }
  };

  const resetFormToProfileData = () => {
    if (!profileData) return;

    let dateOfBirth = null;
    if (profileData.dateOfBirth) {
      dateOfBirth = dayjs.unix(profileData.dateOfBirth);
    }

    form.setFieldsValue({
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      email: profileData.email,
      phoneNumber: profileData.phoneNumber,
      dateOfBirth: dateOfBirth,
      gender: profileData.gender,
      affiliatedHospital: profileData.affiliatedHospital,
      experience: profileData.experience,
      bio: profileData.bio,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Quản lý hồ sơ
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Quản lý thông tin cá nhân và lịch sử y tế của bạn
        </p>
      </div>

      <Card className="shadow-sm mb-6">
        {/* Profile Photo + Availability Row */}
        <div className="grid grid-cols-[1fr_280px] gap-6 mb-6">
          {/* Profile Photo Section */}
          <div>
            <p
              className="text-base font-bold text-gray-900 mb-4"
              style={{ fontSize: "16px", fontWeight: 700 }}
            >
              Ảnh đại diện
            </p>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar
                  size={80}
                  src={profileData?.profileImage}
                  className="border-4 border-white shadow-md"
                >
                  {profileData?.displayName?.charAt(0)}
                </Avatar>
                <Upload
                  beforeUpload={handlePhotoUpload}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button
                    icon={<CameraFilled />}
                    shape="circle"
                    size="small"
                    className="absolute bottom-0 right-0 bg-purple-600 text-white border-0 hover:bg-purple-700"
                    loading={uploadingPhoto}
                  />
                </Upload>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {profileData?.displayName}
                </h2>
                <p className="text-purple-600 text-sm">
                  {profileData?.specialization}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Mã số đăng ký: {profileData?.doctorRegId || "12345-ABC"}
                </p>
              </div>
            </div>
          </div>

          {/* Availability Section */}
          <div>
            <p
              className="text-base font-bold text-gray-900 mb-4"
              style={{ fontSize: "16px", fontWeight: 700 }}
            >
              Tính khả dụng
            </p>
            <div className="bg-purple-50 rounded-lg p-4 flex gap-5">
              <div>
                <p className="text-xs text-gray-500 mb-1">Ngày có sẵn tiếp theo</p>
                <p className="text-base font-semibold text-gray-900 mb-3">
                  {profileData?.nextAvailableTimestamp
                    ? formatNextAvailability(profileData.nextAvailableTimestamp)
                    : "Chưa thiết lập"}
                </p>
              </div>
              <Button
                color="primary"
                variant="link"
                className="text-purple-600 p-0 h-auto font-medium text-sm"
                onClick={() => navigate("/profile/availability")}
              >
                Sửa →
              </Button>
            </div>
          </div>
        </div>

        <Divider className="my-6" />

        {/* Personal Information Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3
              className="text-base font-bold text-gray-900"
              style={{ fontSize: "16px", fontWeight: 700 }}
            >
              Thông tin cá nhân
            </h3>
            <Button
              color="primary"
              variant="link"
              icon={<EditOutlined />}
              onClick={() => setEditingPersonal(!editingPersonal)}
              className="text-purple-600 p-0 h-auto"
            >
              Sửa
            </Button>
          </div>

          <Form
            form={form}
            layout="horizontal"
            labelCol={{ flex: "0 0 140px" }}
            labelAlign="left"
            colon={false}
          >
            <div className="grid grid-cols-2 gap-x-6">
              <div>
                <Form.Item
                  label={
                    <span className="text-xs text-gray-500">Tên</span>
                  }
                  name="firstName"
                  rules={[
                    { required: true, message: "Vui lòng nhập tên" },
                  ]}
                >
                  {editingPersonal ? (
                    <Input />
                  ) : (
                    <span className="text-sm text-gray-900">
                      {form.getFieldValue("firstName") || "-"}
                    </span>
                  )}
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-xs text-gray-500">Họ</span>
                  }
                  name="lastName"
                  rules={[
                    { required: true, message: "Vui lòng nhập họ" },
                  ]}
                >
                  {editingPersonal ? (
                    <Input />
                  ) : (
                    <span className="text-sm text-gray-900">
                      {form.getFieldValue("lastName") || "-"}
                    </span>
                  )}
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-xs text-gray-500">Địa chỉ email</span>
                  }
                  name="email"
                  rules={[
                    { required: true, message: "Vui lòng nhập địa chỉ email" },
                    { type: "email", message: "Email không hợp lệ" },
                  ]}
                >
                  {editingPersonal ? (
                    <Input />
                  ) : (
                    <span className="text-sm text-gray-900">
                      {form.getFieldValue("email") || "-"}
                    </span>
                  )}
                </Form.Item>
              </div>

              <div>
                <Form.Item
                  label={
                    <span className="text-xs text-gray-500">Số điện thoại</span>
                  }
                  name="phoneNumber"
                  rules={[
                    { required: true, message: "Vui lòng nhập số điện thoại" },
                  ]}
                >
                  {editingPersonal ? (
                    <Input />
                  ) : (
                    <span className="text-sm text-gray-900">
                      {form.getFieldValue("phoneNumber") || "-"}
                    </span>
                  )}
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-xs text-gray-500">Ngày sinh</span>
                  }
                  name="dateOfBirth"
                  rules={[
                    { required: true, message: "Vui lòng nhập ngày sinh" },
                  ]}
                >
                  {editingPersonal ? (
                    <DatePicker
                      className="w-full"
                      format="YYYY-MM-DD"
                      suffixIcon={
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      }
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-900">
                        {form
                          .getFieldValue("dateOfBirth")
                          ?.format("YYYY-MM-DD") || "-"}
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </Form.Item>

                <Form.Item
                  label={<span className="text-xs text-gray-500">Giới tính</span>}
                  name="gender"
                  rules={[{ required: true, message: "Vui lòng chọn giới tính" }]}
                >
                  {editingPersonal ? (
                    <Select>
                      <Option value="Male">Nam</Option>
                      <Option value="Female">Nữ</Option>
                    </Select>
                  ) : (
                    <span className="text-sm text-gray-900">
                      {form.getFieldValue("gender") == "Male" ? "Nam" : "Nữ" || "-"}
                    </span>
                  )}
                </Form.Item>
              </div>
            </div>

            {editingPersonal && (
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => {
                    resetFormToProfileData();
                    setEditingPersonal(false);
                  }}
                >
                  Hủy bỏ
                </Button>

                <Button
                  type="primary"
                  onClick={handleSavePersonalInfo}
                  loading={saving}
                  className="bg-purple-600 hover:bg-purple-700 border-0"
                >
                  Lưu thay đổi
                </Button>
              </div>
            )}
          </Form>
        </div>

        <Divider className="my-6" />

        {/* Professional Information Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3
              className="text-base font-bold text-gray-900"
              style={{ fontSize: "16px", fontWeight: 700 }}
            >
              Thông tin chuyên nghiệp
            </h3>
            <Button
              color="primary"
              variant="link"
              icon={<EditOutlined />}
              onClick={() => setEditingProfessional(!editingProfessional)}
              className="text-purple-600 p-0 h-auto"
            >
              Sửa
            </Button>
          </div>

          <Form
            form={form}
            layout="horizontal"
            labelCol={{ flex: "0 0 220px" }}
            labelAlign="left"
            colon={false}
          >
            <div className="grid grid-cols-2 gap-x-6">
              <Form.Item
                label={
                  <span className="text-xs text-gray-500">
                    Bệnh viện hoặc phòng khám liên kết
                  </span>
                }
                name="affiliatedHospital"
                rules={[{ required: true, message: "Vui lòng vào buổi tập luyện" }]}
              >
                {editingProfessional ? (
                  <Input />
                ) : (
                  <span className="text-sm text-gray-900">
                    {form.getFieldValue("affiliatedHospital") || "-"}
                  </span>
                )}
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-xs text-gray-500">
                    Nhiều năm kinh nghiệm
                  </span>
                }
                name="experience"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập số năm kinh nghiệm",
                  },
                ]}
              >
                {editingProfessional ? (
                  <Input type="number" />
                ) : (
                  <span className="text-sm text-gray-900">
                    {form.getFieldValue("experience") || "-"}
                  </span>
                )}
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className="text-xs text-gray-500">
                  Tiểu sử / Mô tả ngắn
                </span>
              }
              name="bio"
              labelCol={{ flex: "0 0 220px" }}
            >
              {editingProfessional ? (
                <TextArea
                  rows={4}
                  placeholder="Một chuyên gia quản lý cân nặng tận tâm và giàu lòng trắc ẩn với hơn 15 năm kinh nghiệm trong việc xây dựng các kế hoạch điều trị cá nhân hóa, dựa trên bằng chứng khoa học cho bệnh nhân."
                />
              ) : (
                <p className="text-sm text-gray-900 leading-relaxed m-0">
                  {form.getFieldValue("bio") || "-"}
                </p>
              )}
            </Form.Item>

            {editingProfessional && (
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => {
                    resetFormToProfileData();
                    setEditingProfessional(false);
                  }}
                >
                  Hủy bỏ
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveProfessionalInfo}
                  loading={saving}
                  className="bg-purple-600 hover:bg-purple-700 border-0"
                >
                  Lưu thay đổi
                </Button>
              </div>
            )}
          </Form>
        </div>
      </Card>

      {/* Security Settings - SEPARATE CARD */}
      <Card className="shadow-sm">
        <h3
          className="text-base font-bold text-gray-900 mb-6"
          style={{ fontSize: "16px", fontWeight: 700 }}
        >
          Cài đặt bảo mật
        </h3>

        <div className="flex items-center justify-between pb-6 border-b border-gray-200">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Mật khẩu</h4>
            <p className="text-xs text-gray-400 mt-1">
              Lần cuối thay đổi{" "}
              {profileData?.passwordChangedAt
                ? formatPasswordChanged(profileData.passwordChangedAt)
                : "never"}
            </p>
          </div>
          <Button
            className="text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
            onClick={() => setChangePasswordVisible(true)}
          >
            Thay đổi mật khẩu
          </Button>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 text-sm mb-1">
                Quyền riêng tư của bạn rất quan trọng
              </h4>
              <p className="text-sm text-blue-800 leading-relaxed">
                Chúng tôi bảo mật và giữ kín thông tin y tế của bạn. Dữ liệu của bạn được mã hóa và chỉ được chia sẻ với các nhà cung cấp dịch vụ chăm sóc sức khỏe khi cần thiết cho quá trình điều trị của bạn.
              </p>
            </div>
          </div>
        </div>
      </Card>

      <ChangePasswordModal
        visible={changePasswordVisible}
        onClose={() => setChangePasswordVisible(false)}
      />
    </div>
  );
};

export default Profile;
